// components/FloatingHelpWidget.tsx
import { AnimatePresence, motion } from "framer-motion";
import {
  HelpCircle,
  X,
  Mic,
  Wifi,
  MessageSquare,
  Zap,
  Info,
} from "lucide-react";
import React, { useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { Button, Card, CardContent, CardHeader } from "./ui";
import ApiModeExplainer from "./ApiModeExplainer";

const FloatingHelpWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "detailed">(
    "overview"
  );

  const liveAPIConnected = useAppStore((state) => state.liveAPIConnected);
  const aiProvider = useAppStore((state) => state.aiProvider);
  const voiceState = useAppStore((state) => state.voiceState);
  const isLoading = useAppStore((state) => state.isLoading);

  const getCurrentStatus = () => {
    if (voiceState.isRecording)
      return { icon: Mic, text: "Recording", color: "text-red-400" };
    if (isLoading)
      return { icon: Zap, text: "Processing", color: "text-blue-400" };
    if (voiceState.isPlaying)
      return { icon: Mic, text: "Speaking", color: "text-green-400" };
    if (liveAPIConnected)
      return { icon: Wifi, text: "Live API", color: "text-green-400" };
    return {
      icon: MessageSquare,
      text: "Standard API",
      color: "text-blue-400",
    };
  };

  const status = getCurrentStatus();

  return (
    <>
      {/* Floating Help Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
      >
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <Button
            onClick={() => setIsOpen(!isOpen)}
            size="lg"
            className={`rounded-full w-14 h-14 shadow-2xl border-2 ${
              isOpen
                ? "bg-blue-600 border-blue-400"
                : "bg-gradient-to-br from-blue-600 to-purple-600 border-blue-400/30"
            } hover:shadow-blue-500/25 transition-all duration-300`}
          >
            <AnimatePresence mode="wait">
              {isOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="h-6 w-6" />
                </motion.div>
              ) : (
                <motion.div
                  key="help"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <HelpCircle className="h-6 w-6" />
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </motion.div>

        {/* Status indicator on the button */}
        <motion.div
          className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <status.icon className={`h-2 w-2 ${status.color}`} />
        </motion.div>
      </motion.div>

      {/* Help Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-24 right-6 z-40 w-96 max-w-[calc(100vw-3rem)]"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          >
            <Card className="bg-slate-800/95 backdrop-blur-xl border-slate-700 shadow-2xl">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Info className="h-5 w-5 text-blue-400" />
                    <h3 className="font-semibold text-white">Voice AI Help</h3>
                  </div>
                  <div className="flex items-center space-x-1 text-xs">
                    <status.icon className={`h-3 w-3 ${status.color}`} />
                    <span className={status.color}>{status.text}</span>
                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex space-x-1 bg-slate-700/50 rounded-lg p-1">
                  <button
                    onClick={() => setActiveTab("overview")}
                    className={`flex-1 px-3 py-1 text-xs rounded-md transition-all ${
                      activeTab === "overview"
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    Quick Guide
                  </button>
                  <button
                    onClick={() => setActiveTab("detailed")}
                    className={`flex-1 px-3 py-1 text-xs rounded-md transition-all ${
                      activeTab === "detailed"
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    API Details
                  </button>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <AnimatePresence mode="wait">
                  {activeTab === "overview" ? (
                    <motion.div
                      key="overview"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="space-y-4"
                    >
                      {/* Current Mode Status */}
                      <div
                        className={`p-3 rounded-lg border ${
                          liveAPIConnected
                            ? "bg-green-500/10 border-green-500/30"
                            : "bg-blue-500/10 border-blue-500/30"
                        }`}
                      >
                        <div className="flex items-center space-x-2 mb-2">
                          {liveAPIConnected ? (
                            <Wifi className="h-4 w-4 text-green-400" />
                          ) : (
                            <MessageSquare className="h-4 w-4 text-blue-400" />
                          )}
                          <span className="font-medium text-white">
                            {liveAPIConnected
                              ? "Live API Mode"
                              : "Standard API Mode"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-300">
                          {liveAPIConnected
                            ? "Real-time voice streaming with ultra-low latency. You can interrupt the AI naturally."
                            : "Voice is transcribed to text, processed, then converted back to speech. More reliable but higher latency."}
                        </p>
                      </div>

                      {/* Quick Actions */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-white text-sm">
                          How to use:
                        </h4>
                        <div className="space-y-2 text-xs text-slate-300">
                          <div className="flex items-start space-x-2">
                            <div className="w-1 h-1 bg-blue-400 rounded-full mt-2"></div>
                            <span>
                              Click the <strong>microphone button</strong> to
                              start voice input
                            </span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <div className="w-1 h-1 bg-blue-400 rounded-full mt-2"></div>
                            <span>
                              Type messages in the text input for text
                              conversation
                            </span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <div className="w-1 h-1 bg-blue-400 rounded-full mt-2"></div>
                            <span>
                              Use the <strong>volume button</strong> to
                              mute/unmute AI voice responses
                            </span>
                          </div>
                          {aiProvider === "gemini" && (
                            <div className="flex items-start space-x-2">
                              <div className="w-1 h-1 bg-green-400 rounded-full mt-2"></div>
                              <span>
                                Connect to <strong>Live API</strong> for
                                real-time voice conversations
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Tips */}
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <h4 className="font-medium text-white text-sm mb-2">
                          💡 Tips:
                        </h4>
                        <ul className="space-y-1 text-xs text-slate-300">
                          <li>
                            • Wait for the AI to finish speaking before
                            recording
                          </li>
                          <li>• Speak clearly and at normal volume</li>
                          <li>
                            • Use Live API for the most natural conversations
                          </li>
                          {!liveAPIConnected && (
                            <li>
                              • Standard mode works with both OpenAI and Gemini
                            </li>
                          )}
                        </ul>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="detailed"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="max-h-80 overflow-y-auto"
                    >
                      <ApiModeExplainer
                        isLiveAPIConnected={liveAPIConnected}
                        aiProvider={aiProvider}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingHelpWidget;
